// Shared mapping of failed admin-API responses to user-friendly messages.
//
// The backend enforces the three-role model server-side, so a Staff user with
// only "View" on a tab who attempts a write (create/update/delete) gets a 403.
// Surfacing the raw body (e.g. Python's `{"error": "Forbidden"}`) is confusing,
// so callers route write failures through this helper to show a clear,
// actionable message instead.

export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to make changes. Please contact your administrator for edit access.";

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again.';

/**
 * friendlyApiError(status, data, fallback)
 *
 * Returns a human-friendly message for a failed response:
 *   - 403 -> permission-denied message (view-only user attempted a change)
 *   - 401 -> session-expired message
 *   - otherwise -> the body's `error`/`message` if present, else `fallback`.
 *
 * `data` may be a parsed object, a raw string body, or null/undefined.
 */
export function friendlyApiError(status, data, fallback) {
  const code = status === 403 || status === 401 ? status : Number(status);
  if (code === 403) return PERMISSION_DENIED_MESSAGE;
  if (code === 401) return SESSION_EXPIRED_MESSAGE;

  let raw = '';
  if (data && typeof data === 'object') {
    raw = data.error || data.message || '';
  } else if (typeof data === 'string') {
    raw = data;
  }
  raw = String(raw || '').trim();

  // A body that is itself the verbatim "Forbidden" payload still maps to the
  // friendly permission message even if the status was lost along the way.
  if (raw === 'Forbidden' || raw === '{"error": "Forbidden"}' || raw === '{"error":"Forbidden"}') {
    return PERMISSION_DENIED_MESSAGE;
  }

  return raw || fallback || 'Something went wrong. Please try again.';
}
